/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';

import debounce from 'lodash/debounce';
import { decode } from '@googlemaps/polyline-codec';

import { BOUNDING_BOX_PADDINGS, INITIAL_MAP_STATUS, SAFE_AREA_PADDINGS } from '../constants';
import { FILTERS } from '../../../constants';
import { useStaticProps } from '../../context/static-props';
import { EVENTS, getBounds, getBoundsFromCoordinates, padBounds, POLYGON_EVENTS } from '../lib';
import { serializeItemLocation } from '../../context/filters/helpers';
import { usePolygonStatus } from '../context/polygon-context';
import usePolygonDrawing from './use-polygon-drawing';
import usePolygonComplete from './use-polygon-complete';
import usePolygonSession from './use-polygon-session';
import useDrawable from './use-drawable';
import useMapConfig from './use-map-config';
import { trackClickEvent } from '../track';
import getHistory from '../../../lib/history';

const { BOUNDS_CHANGED, DRAG_END, IDLE, ZOOM_CHANGED } = EVENTS;
const { IDLE: POLYGON_IDLE, DRAWING, DRAWN } = POLYGON_EVENTS;
const GET_RESULTS_DEBOUNCE_MS = 500;
const POLYGON_COORDINATES_PRECISION = 5;

// eslint-disable-next-line import/no-mutable-exports
let savedItemLocation = null;

const useMapState = ({
  fitMarkersInMap,
  isLoadingResults,
  markers,
  updateResults,
  fixedCoords,
  selectedMarker = null,
  setSelectedMarker = () => undefined,
  panToSelectedMarker = false,
  onPolygonError = () => undefined,
}) => {
  const { deviceType } = useStaticProps();
  const { searchTrack } = useMapConfig();
  const [map, setMap] = useState(null);
  const [mapEvent, setMapEvent] = useState(null);
  const [mapStatus, setMapStatus] = useState(INITIAL_MAP_STATUS);
  const [isPolygonDrawing, setIsPolygonDrawing] = useState(false);
  const {
    polygonCompleted,
    forceFitMarkers,
    setForceFitMarkers,
    setPolygonCompleted,
    restoreSavedBounds,
    setRestoreSavedBounds,
  } = usePolygonStatus();
  const [polygonTogglerState, setPolygonTogglerState] = useState(POLYGON_IDLE);
  const [pendingPolygonUpdate, setPendingPolygonUpdate] = useState(false);

  const {
    saveLocationBeforePolygon,
    getSavedLocation,
    getSavedMapBounds,
    clearSavedState,
    markPolygonAction,
    markItemLocationInjected,
    saveCenteredPolygonBounds,
    getCenteredPolygonBounds,
    wasLastActionPolygon,
  } = usePolygonSession();

  const { onPolygonComplete } = usePolygonComplete({
    mapInstance: map,
    setPolygonTogglerState,
    saveLocationBeforePolygon,
    markItemLocationInjected,
    saveCenteredPolygonBounds,
  });

  const history = getHistory();

  const { clearPolygon, drawPolygonFromCoordinates } = usePolygonDrawing({
    map,
    drawable: polygonTogglerState === DRAWING,
    onPolygonComplete,
    onPolygonError,
  });

  const handleDrawable = useDrawable(
    map,
    polygonTogglerState,
    setPolygonTogglerState,
    setIsPolygonDrawing,
    clearPolygon,
    saveLocationBeforePolygon,
    getSavedLocation,
    markPolygonAction,
    history,
    getCenteredPolygonBounds,
  );

  const isUIReady = !isLoadingResults && !!mapEvent;

  const isPolygonInUse = isPolygonDrawing || polygonCompleted;

  const getNewResults = useCallback(
    debounce(() => {
      updateResults(padBounds(map, BOUNDING_BOX_PADDINGS[deviceType]).toJSON());
      setMapEvent(IDLE);
    }, GET_RESULTS_DEBOUNCE_MS),
    [updateResults, map, setMapEvent],
  );

  const handleMapLoad = (mapInstance) => setMap(mapInstance);

  const handleDragEnd = () => {
    if (isPolygonInUse) {
      return;
    }

    setMapEvent(DRAG_END);
    getNewResults();
  };

  const handleIdle = () => {
    if (!mapEvent && !pendingPolygonUpdate) {
      setMapEvent(IDLE);

      return;
    }

    if (pendingPolygonUpdate) {
      setPendingPolygonUpdate(false);
      updateResults(padBounds(map, BOUNDING_BOX_PADDINGS[deviceType]).toJSON());
      setMapEvent(IDLE);
    }
  };

  // We need to differentiate between an user and a programmatically triggered zoom_changed event
  // (e.g. when fitMarkersInMap is true).
  // Looks like isUIReady works to differentiate initial zoom event from the rest
  const handleZoomChanged = () => {
    if (isPolygonInUse) {
      return;
    }

    if (isUIReady) {
      setMapEvent(ZOOM_CHANGED);
      getNewResults();
    }
  };

  // The bounds_changed event is always triggered after the zoom_changed event, so we need to
  // check whether the zoom_changed event was triggered by the user or not.
  const handleBoundsChanged = () => {
    if (isPolygonInUse) {
      return;
    }

    if (mapEvent === ZOOM_CHANGED || mapEvent === DRAG_END) {
      setMapEvent(BOUNDS_CHANGED);
      getNewResults();
    }
  };

  useEffect(() => {
    const { search } = history.location;
    const params = new URLSearchParams(search);
    const hasPolygonLocation = params.get(FILTERS.IDS.POLYGON_LOCATION);

    if (hasPolygonLocation && map) {
      const decodedHash = decode(hasPolygonLocation, POLYGON_COORDINATES_PRECISION);

      const polygon = drawPolygonFromCoordinates(map, decodedHash);

      setPolygonTogglerState(DRAWN);
      setPolygonCompleted(true);

      if (polygon) {
        const polygonBounds = getBoundsFromCoordinates(decodedHash);

        if (polygonBounds) {
          const tempListener = map.addListener('bounds_changed', () => {
            window.google.maps.event.removeListener(tempListener);
            updateResults(padBounds(map, BOUNDING_BOX_PADDINGS[deviceType]).toJSON());
          });

          map.fitBounds(polygonBounds, BOUNDING_BOX_PADDINGS[deviceType]);
        } else {
          updateResults(padBounds(map, BOUNDING_BOX_PADDINGS[deviceType]).toJSON());
        }
      } else {
        updateResults(padBounds(map, BOUNDING_BOX_PADDINGS[deviceType]).toJSON());
      }
    }
  }, [map, updateResults, deviceType]);

  useEffect(() => {
    const handlePolygonClear = () => {
      setTimeout(() => {
        clearPolygon();
        setPolygonTogglerState(POLYGON_IDLE);
        setIsPolygonDrawing(false);
        setPolygonCompleted(false);
      }, 0);
    };

    window.addEventListener('polygon:clear', handlePolygonClear);

    return () => {
      window.removeEventListener('polygon:clear', handlePolygonClear);
    };
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.setOptions({
      draggable: polygonTogglerState !== DRAWING,
      scrollwheel: true,
      gestureHandling: 'auto',
    });
  }, [polygonTogglerState, map]);

  useEffect(() => {
    if (isLoadingResults) {
      setSelectedMarker(null, null);
    }
  }, [isLoadingResults]);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (restoreSavedBounds) {
      setRestoreSavedBounds(false);
      setMapEvent(null);

      const savedBounds = getSavedMapBounds();

      if (savedBounds) {
        map.fitBounds(savedBounds, BOUNDING_BOX_PADDINGS[deviceType]);
        clearSavedState();
      } else {
        map.fitBounds(getBounds(markers, fixedCoords), BOUNDING_BOX_PADDINGS[deviceType]);
      }

      return;
    }

    if (forceFitMarkers) {
      setForceFitMarkers(false);
      setMapEvent(null);
      map.fitBounds(getBounds(markers, fixedCoords), BOUNDING_BOX_PADDINGS[deviceType]);

      return;
    }

    if (!fitMarkersInMap || isPolygonInUse) {
      return;
    }

    const { search } = history.location;
    const params = new URLSearchParams(search);
    const hasPolygonLocation = params.get(FILTERS.IDS.POLYGON_LOCATION);

    if (hasPolygonLocation) {
      return;
    }

    if (wasLastActionPolygon()) {
      updateResults(padBounds(map, BOUNDING_BOX_PADDINGS[deviceType]).toJSON());

      return;
    }

    setMapEvent(null);
    map.fitBounds(getBounds(markers, fixedCoords), BOUNDING_BOX_PADDINGS[deviceType]);
  }, [map, markers, fitMarkersInMap, fixedCoords, restoreSavedBounds, isPolygonInUse]);

  useEffect(() => {
    if (isPolygonDrawing) {
      return;
    }

    if (map && (isUIReady || polygonCompleted)) {
      const bounds = map.getBounds();

      setMapStatus({
        bounds,
        zoom: map.getZoom(),
        mapInstance: map,
      });

      if (bounds && !savedItemLocation) {
        const boundsJson = bounds.toJSON();

        savedItemLocation = serializeItemLocation(boundsJson);
      }
    }
  }, [map, isUIReady, isPolygonDrawing, polygonCompleted]);

  useEffect(() => {
    if (map && panToSelectedMarker && selectedMarker !== null && markers[selectedMarker]) {
      const { latitude, longitude } = markers[selectedMarker];
      const markerBounds = getBounds([{ latitude, longitude }]);

      map.panToBounds(markerBounds, SAFE_AREA_PADDINGS[deviceType]);
    }
  }, [panToSelectedMarker, map, markers, selectedMarker, deviceType]);

  /**
   * Emits tracking event for polygon completion after new results are obtained.
   * This follows the same pattern as other filters: apply filter → get new results → emit track.
   * We listen to searchTrack changes to ensure we have the updated track from MDE.
   */
  useEffect(() => {
    if (isUIReady && wasLastActionPolygon() && searchTrack) {
      const track = {
        melidata_track: {
          ...searchTrack,
          type: 'view',
        },
      };

      trackClickEvent(null, track);
    }
  }, [isUIReady, searchTrack]);

  return {
    handleDragEnd,
    handleMapLoad,
    handleZoomChanged,
    handleBoundsChanged,
    handleIdle,
    mapStatus,
    savedItemLocation,
    setIsPolygonDrawing,
    polygonTogglerState,
    setPolygonTogglerState,
    handleDrawable,
    clearPolygon,
    drawPolygonFromCoordinates,
  };
};

export { useMapState, savedItemLocation };
